<script setup lang="ts">
    import EventIcon from './EventIcon.vue';
    import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import FeatureLock from '@shared/components/FeatureLock.vue';
    import Modal from '@shared/components/Modal.vue';
    import GenerateQrCode from '@shared/components/GenerateQrCode.vue';
    import ShareUrlSettings from '@shared/components/ShareUrlSettings.vue';
    import ThemesViewAndSelect from '@cwd/Themes/ThemesViewAndSelect.vue';
    import EventEditorLandingPageTranslations from '@views/Event/EventEditor/LandingPage/EventEditorLandingPageTranslations.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import { combineUrlSegments } from '@shared/utils/combineUrlSegments';
    import { stripHtml } from '@shared/utils/DOM';
    import { copyToClipboard } from '@views/Web/copyToClipboard';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { getDesignThemes } from '@cwd/Themes/Themes.access';
    import { type DesignTheme } from '@cwd/Themes/ThemesTypes';
    import { type Event, EventStatusNames } from '@views/Event/EventTypes';
    import { eventKey, queueEventPropertyKey, saveEventPropertyKey, canEditKey } from '@views/Event/EventEditor/event-editor-keys';

    const props = defineProps<{
        eventId: string;
    }>();

    const event = inject(eventKey)!;
    const canEdit = inject(canEditKey)!;
    const saveEventProperty = inject(saveEventPropertyKey)!;
    const queueEventProperty = inject(queueEventPropertyKey)!;

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);
    const publicHost = computed(() => contextAccess.state.currentAccount!.PublicEventUrl.PublicHost);

    const landingPageUrl = computed(() => combineUrlSegments(publicHost.value, 'Events', props.eventId));
    const previewUrl = computed(() => combineUrlSegments(publicHost.value, 'Events', props.eventId, '?preview=true'));
    const isActive = computed(() => event.value?.Status === EventStatusNames.Active);

    const showQrCodeModal = ref(false);
    const showThemeModal = ref(false);

    const socialTitle = ref('');
    const socialDescription = ref('');

    watch(
        () => event.value?.SocialTitle,
        (value) => (socialTitle.value = value ?? ''),
        { immediate: true }
    );
    watch(
        () => event.value?.SocialDescription,
        (value) => (socialDescription.value = value ?? ''),
        { immediate: true }
    );

    const propertyLabel = (property: string) => $Resources['PropertyEvent' + property + 'Label'];

    async function save(property: string, value: string | null) {
        await saveEventProperty(property, value, propertyLabel(property));
        reloadIframeContent();
    }

    // Share settings only affect meta tags, so the preview doesn't need reloading.
    function saveShareSetting(property: string, value: string | null) {
        return saveEventProperty(property, value, propertyLabel(property));
    }

    // Parity with the old KO GetEventImageUrls: the hero image is the suggested share image.
    const shareImageUrls = computed(() => (event.value?.HeroImageUrl ? [event.value.HeroImageUrl] : []));

    function onSocialTitleChange(newValue: string) {
        socialTitle.value = newValue;
        queueEventProperty('SocialTitle', newValue, propertyLabel('SocialTitle'));
    }

    function onSocialDescriptionChange(newValue: string) {
        socialDescription.value = newValue;
        queueEventProperty('SocialDescription', newValue, propertyLabel('SocialDescription'));
    }

    // Theme
    const themes = ref<DesignTheme[]>([]);
    const selectedThemeName = computed(() => themes.value.find((theme) => theme.DesignThemeId === event.value?.DesignThemeId)?.Name ?? null);

    onMounted(async () => {
        themes.value = await getDesignThemes();
    });

    function onThemeSelected(id: string) {
        save('DesignThemeId', id);
        showThemeModal.value = false;
    }

    // Preview iframe: same postMessage protocol as the old editor (see Scripts/getIframeHeight.js).
    const iframeId = 'landingpage-iframe';
    const iframeEl = ref<HTMLIFrameElement | null>(null);

    function postToIframe(message: Record<string, unknown>) {
        iframeEl.value?.contentWindow?.postMessage(message, '*');
    }

    function reloadIframeContent() {
        postToIframe({ reloadIframeContent: true });
    }

    function onIframeLoad() {
        postToIframe({ getIframeHeight: { iframeId } });
    }

    function onTranslationTextChanged(data: { key: string; value: string }) {
        postToIframe({ updateIframeText: data });
    }

    function onMessage(message: MessageEvent) {
        if (!message.data?.iframeHeight || message.data.iframeId !== iframeId || !iframeEl.value) return;
        iframeEl.value.style.height = message.data.iframeHeight + 'px';
    }

    onMounted(() => window.addEventListener('message', onMessage));
    onBeforeUnmount(() => window.removeEventListener('message', onMessage));
</script>

<template>
    <div v-if="event">
        <h2 class="h3 m-0-t m-lg-b">{{ $Resources.EditEventDetailsLandingPageHeading }}</h2>

        <div class="form-group">
            <FormCheckable
                :label="$Resources.EditEventDetailsToggleLandingPageLabel"
                :switchStyle="SwitchStyle.Standard"
                :checked="event.LandingPageIsPublished"
                :disabled="!canEdit"
                @update:modelValue="(newValue: boolean) => save('LandingPageIsPublished', String(newValue))" />

            <div v-if="event.LandingPageIsPublished && isActive" class="m-sm-t">
                <span class="label-look d-block">{{ $Resources.EventUrlToLandingPageLabel }}:</span>
                <a :href="landingPageUrl" target="_blank" rel="noopener">{{ landingPageUrl }}</a>
                <button type="button" class="btn btn-subtle btn-xs m-xs-l" @click="copyToClipboard(landingPageUrl)">
                    {{ $Resources.CopyUrlToClipBoardBtnText }}
                </button>
            </div>
        </div>

        <template v-if="event.LandingPageIsPublished">
            <div class="form-group">
                <FormCheckable
                    :label="$Resources.EditEventDetailsToggleTimeZoneLabel"
                    :switchStyle="SwitchStyle.Standard"
                    :checked="event.PublicTimeZone"
                    :disabled="!canEdit"
                    @update:modelValue="(newValue: boolean) => save('PublicTimeZone', String(newValue))" />
            </div>

            <div class="form-group m-xl-b">
                <FormCheckable
                    :label="$Resources.EditEventLandingPageCountdownToggleLabel"
                    :switchStyle="SwitchStyle.Standard"
                    :checked="event.UseCountDownInLandingPage"
                    :disabled="!canEdit"
                    @update:modelValue="(newValue: boolean) => save('UseCountDownInLandingPage', String(newValue))" />

                <p class="m-sm-t">{{ $Resources.EditEventLandingPageCountdownToggleInfoText }}</p>
            </div>

            <div class="d-flex flex-wrap align-items-center gap-sm m-lg-b">
                <button type="button" class="btn btn-subtle m-auto-r" @click="showQrCodeModal = true">
                    <span class="glyphicon glyphicon-qrcode m-xs-r" aria-hidden="true"></span>
                    {{ $Resources.EventGenerateQrCode }}
                </button>

                <a role="button" class="btn btn-subtle btn-flex" :href="previewUrl" target="_blank" rel="noopener">
                    {{ $Resources.EventLinkToPreviewLandingPageLabel }}
                    <EventIcon icon="openInNew" :size="16" />
                </a>

                <a
                    role="button"
                    class="btn btn-primary btn-flex"
                    :class="{ 'disabled pe-none': !isActive }"
                    :href="landingPageUrl"
                    target="_blank"
                    rel="noopener">
                    {{ $Resources.EventLinkToLandingPageLabel }}
                    <EventIcon icon="openInNew" :size="16" />
                </a>
            </div>

            <div class="d-flex flex-wrap gap-lg">
                <div style="width: 480px">
                    <EventEditorLandingPageTranslations :event="event" :updateEnabled="canEdit" @textChanged="onTranslationTextChanged" />

                    <FeatureLock feature="Account.Themes" showUpgradeAlert :infoTitle="$Resources.SelectThemeHeading">
                        <div class="event-card">
                            <h3 class="event-card-heading">
                                {{ $Resources.SelectThemeHeading }}
                            </h3>

                            <p>
                                {{ $Resources.ThemesListSelectedLabel }}:
                                <strong>{{ event.DesignThemeId === null ? $Resources.DefaultThemeLabel : selectedThemeName }}</strong>
                            </p>

                            <div class="form-group d-flex flex-wrap gap-xs">
                                <button type="button" class="btn btn-default" :disabled="!canEdit" @click="showThemeModal = true">
                                    {{ $Resources.SelectThemeLabel }}
                                </button>
                                <button
                                    v-if="event.DesignThemeId"
                                    type="button"
                                    class="btn btn-default"
                                    :disabled="!canEdit"
                                    @click="save('DesignThemeId', null)">
                                    {{ $Resources.UseDefaultThemeLabel }}
                                </button>
                            </div>
                        </div>
                    </FeatureLock>

                    <ShareUrlSettings
                        :imageUrls="shareImageUrls"
                        :imageUrl="event.SocialImageUrl"
                        :title="socialTitle"
                        :titleFallback="event.Name"
                        :description="socialDescription"
                        :descriptionFallback="stripHtml(event.PublicDescription)"
                        issueType="event"
                        :disabled="!canEdit"
                        @update:imageUrl="(newValue) => saveShareSetting('SocialImageUrl', newValue)"
                        @update:title="onSocialTitleChange"
                        @update:description="onSocialDescriptionChange" />
                </div>

                <div class="flex-1 m-lg-b" style="min-width: 375px">
                    <iframe ref="iframeEl" :id="iframeId" loading="lazy" scrolling="no" :src="previewUrl" @load="onIframeLoad"></iframe>
                </div>
            </div>
        </template>
    </div>

    <Modal :title="$Resources.EventGenerateQrCode" :show="showQrCodeModal" size="md" @close="showQrCodeModal = false">
        <template #content>
            <GenerateQrCode :url="landingPageUrl" />
        </template>
    </Modal>

    <Modal :title="$Resources.SelectThemeLabel" :show="showThemeModal" size="lg" @close="showThemeModal = false">
        <template #content>
            <ThemesViewAndSelect
                :currentThemeId="event?.DesignThemeId ?? ''"
                :eventId="eventId"
                @themeCreated="showThemeModal = false"
                @themeSelected="(id: string) => onThemeSelected(id)"
                @showModal="showThemeModal = true"
                @getThemes="(newValue: DesignTheme[]) => (themes = newValue)" />
        </template>
    </Modal>
</template>
